/**
 * Pure view-model derivation for the Resolved panel (spec 105).
 *
 * The panel itself reads ZERO state directly — it consumes the output of
 * `buildResolvedPanelViewModel` from props supplied by `MarketDetailLayout`.
 * Keeping derivation in a non-React module means it can be unit-tested in
 * isolation and the panel becomes a pure render of the view-model.
 *
 * Every value the panel shows is a SNAPSHOT AT RESOLUTION — the conviction the
 * winning/losing outcomes held when the market resolved, and the volume /
 * participants accumulated up to `resolved_ts`. Post-resolution drift
 * (graduation trading, late conviction moves) is intentionally excluded.
 *
 * See `frontend/specs/105-resolved-panel/supplementary/data-model.md` for the
 * authoritative shape definitions and validation rules covered by
 * `derive.test.ts`.
 */

// ---------------------------------------------------------------------------
// Input types — what `MarketDetailLayout` passes to `<ResolvedPanel>`.
// ---------------------------------------------------------------------------

export interface OutcomeInput {
  outcomeIndex: number;
  label: string;
  /** Outcome ticker. Null when unavailable; the ticker chip is skipped. */
  ticker: string | null;
  /** Outcome SPL mint — `outcomes[].mint`. */
  mint: string | null;
  imageUrl: string | null;
  imageNsfw: boolean;
  /** Snapshot conviction at resolution (PPB/D9 string) —
   *  `outcomes[].convictionAtResolution`. The "Final" column / consensus stat.
   *  Falls back to the live anchor only when the snapshot is absent. */
  convictionAtResolution: string | null;
  /** Live conviction anchor (PPB/D9 string) — `outcomes[].convictionAnchor`.
   *  Fallback for `convictionAtResolution` on legacy rows. */
  convictionAnchor: string | null;
  /** Snapshot peak conviction at/before resolution (PPB/D9 string) —
   *  `outcomes[].peakAtResolution`. The "Peak" column. */
  peakAtResolution: string | null;
  /** Lifetime peak (PPB/D9 string) — `outcomes[].convictionPeak`. Fallback for
   *  `peakAtResolution` on legacy rows. */
  convictionPeak: string | null;
  /** Volume on this outcome at resolution (D6 string) —
   *  `outcomes[].volumeAtResolution`. The "Volume" column; excludes
   *  post-graduation AMM volume. Falls back to lifetime on legacy rows. */
  volumeAtResolutionD6: string | null;
  /** Lifetime volume on this outcome (D6 string) — `outcomes[].volumeTotal`.
   *  Fallback for `volumeAtResolutionD6`. */
  volumeTotalD6: string | null;
}

export interface ResolvedPanelProps {
  /** The market pubkey — used by leaf components to build the `OptimizedImage`
   *  outcome entity, and the winner-page "View market" link. */
  marketPubkey: string;
  marketStatus: string;
  winnerOutcomeIndex: number | null;
  outcomes: ReadonlyArray<OutcomeInput>;
  resolvedAtIso: string | null;
  /** Volume accumulated up to `resolved_ts` (D6 string) —
   *  `market.volumeAtResolution`. Null when the backend has not populated it. */
  volumeAtResolutionRaw: string | null;
  /** Distinct traders up to `resolved_ts` — `market.uniqueTradersAtResolution`. */
  uniqueTradersAtResolution: number | null;
  /** Which surface renders the content. `'market'` (default) is the standalone
   *  panel, `'market-tab'` is its bare content inside market-detail tabs, and
   *  `'winner-page'` is the
   *  "RESOLVED FROM MARKET · WINNER $X" recap on `/winners/{mint}`. */
  variant?: 'market' | 'market-tab' | 'winner-page';
  /** Parent market title, shown in the winner-page header or above the winner identity. */
  marketTitle?: string;
  /** Parent market identity used by the `'winner-page'` header. */
  marketTicker?: string | null;
  marketCategory?: string;
  marketCoverImageUrl?: string | null;
  marketCoverImageNsfw?: boolean;
  /** Linked live event shown only in the winner-page recap. */
  eventAssociation?: {
    event: import('@/components/live/types').LiveEventSummary;
    eventLogo: import('@/components/live/event-identity').EventIdentityAsset | null;
    participantLogos?:
      | readonly import('@/components/live/event-identity').EventIdentityAsset[]
      | null
      | undefined;
    timeZone: string;
  };
}

// ---------------------------------------------------------------------------
// Output view-model — what the panel renders from.
// ---------------------------------------------------------------------------

export interface WinnerVM {
  index: number;
  label: string;
  /** Outcome ticker for the chip + the winner-page "WINNER $X" header. */
  ticker: string | null;
  /** Outcome SPL mint. */
  mint: string | null;
  imageUrl: string | null;
  imageNsfw: boolean;
}

export interface StatsVM {
  resolvedAt: Date | null;
  /** Volume at resolution (D6). */
  volumeTotalD6: bigint | null;
  /** Distinct participants at resolution. */
  uniqueTradersTotal: number | null;
  /** The winning outcome's conviction at resolution, as percent — "what it hit
   *  when it resolved". Null when unparseable. */
  consensusPct: number | null;
}

export interface OutcomeRowVM {
  index: number;
  label: string;
  ticker: string | null;
  imageUrl: string | null;
  imageNsfw: boolean;
  /** Final share at resolution, as percent (sortable). The table "Final" column. */
  finalSharePct: number;
  /** Peak conviction at resolution, as percent; null when unavailable. The
   *  table "Peak" column. */
  peakConsensusPct: number | null;
  /** Volume on this outcome (D6 string); null when absent. The "Volume" column. */
  volumeTotalD6: string | null;
  /** True for the winning outcome (rendered first, with a winner marker). */
  isWinner: boolean;
}

/** Lifecycle marker on the winner header — "Graduating" while a resolved market
 *  is on its way to graduation; no badge once graduated. */
export type ResolvedStatusBadge = 'graduating' | null;

export interface ResolvedPanelViewModel {
  winner: WinnerVM;
  stats: StatsVM;
  /** Every outcome as a table row — the winner first (rank 1), then losers
   *  descending by final share. */
  outcomeRows: ReadonlyArray<OutcomeRowVM>;
  /** Winner-header lifecycle marker. */
  statusBadge: ResolvedStatusBadge;
}

// ---------------------------------------------------------------------------
// Helpers — exported for tests if a future need arises; lightweight + total.
// ---------------------------------------------------------------------------

import { convictionPercent } from '@morfi/backend-sdk-v2/client';

export function parseConvictionAnchorToPct(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  if (!Number.isFinite(Number(raw))) return null;
  // The SDK owns the conviction→% math (the single source of truth). It returns
  // the REAL conviction (ppb/1e9×100), unclamped and never normalised — the
  // winner crossing the 100% threshold (e.g. 127.7%) is the whole point of a
  // conviction market.
  return convictionPercent(raw);
}

export function parseD6Bigint(raw: string | null | undefined): bigint | null {
  if (raw === null || raw === undefined) return null;
  try {
    return BigInt(raw);
  } catch {
    // SILENT-CATCH: total parser — malformed input from the SDK is rendered
    // as zero rather than crashing the panel.
    return null;
  }
}

export function parseIsoDate(raw: string | null | undefined): Date | null {
  if (raw === null || raw === undefined) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** Resolution snapshot first, live value as the legacy fallback. */
function snapshotPct(snapshot: string | null, live: string | null): number | null {
  return parseConvictionAnchorToPct(snapshot) ?? parseConvictionAnchorToPct(live);
}

// ---------------------------------------------------------------------------
// Main entry point.
// ---------------------------------------------------------------------------

const RESOLVED_STATUSES: ReadonlySet<string> = new Set(['resolved', 'graduated']);

export function buildResolvedPanelViewModel(
  props: ResolvedPanelProps,
): ResolvedPanelViewModel | null {
  // FR-001 — status gate.
  if (!RESOLVED_STATUSES.has(props.marketStatus)) return null;

  // FR-012 — winner-known gate.
  if (props.winnerOutcomeIndex === null) return null;

  // EC-2 — fail closed if the winner outcome is missing from the list.
  const winnerOutcome = props.outcomes.find((o) => o.outcomeIndex === props.winnerOutcomeIndex);
  if (winnerOutcome === undefined) return null;

  const winner: WinnerVM = {
    index: winnerOutcome.outcomeIndex,
    label: winnerOutcome.label,
    ticker: winnerOutcome.ticker,
    mint: winnerOutcome.mint,
    imageUrl: winnerOutcome.imageUrl,
    imageNsfw: winnerOutcome.imageNsfw,
  };

  const toRow = (o: OutcomeInput, isWinner: boolean): OutcomeRowVM => ({
    index: o.outcomeIndex,
    label: o.label,
    ticker: o.ticker,
    imageUrl: o.imageUrl,
    imageNsfw: o.imageNsfw,
    finalSharePct: snapshotPct(o.convictionAtResolution, o.convictionAnchor) ?? 0,
    peakConsensusPct: snapshotPct(o.peakAtResolution, o.convictionPeak),
    volumeTotalD6: o.volumeAtResolutionD6 ?? o.volumeTotalD6,
    isWinner,
  });

  // The winner is always rank 1; losers follow, sorted desc by final share.
  const loserRows: OutcomeRowVM[] = props.outcomes
    .filter((o) => o.outcomeIndex !== props.winnerOutcomeIndex)
    .map((o) => toRow(o, false))
    .sort((a, b) => b.finalSharePct - a.finalSharePct);
  const outcomeRows: OutcomeRowVM[] = [toRow(winnerOutcome, true), ...loserRows];

  const stats: StatsVM = {
    resolvedAt: parseIsoDate(props.resolvedAtIso),
    volumeTotalD6: parseD6Bigint(props.volumeAtResolutionRaw),
    uniqueTradersTotal: props.uniqueTradersAtResolution,
    consensusPct: snapshotPct(winnerOutcome.convictionAtResolution, winnerOutcome.convictionAnchor),
  };

  // Lifecycle marker: a resolved market is graduating; once graduated, no badge.
  const statusBadge: ResolvedStatusBadge = props.marketStatus === 'resolved' ? 'graduating' : null;

  return { winner, stats, outcomeRows, statusBadge };
}
